import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type DocumentFilterJSON = z.infer<typeof DocumentFilterSchema.DocumentFilterJSONZ>;
export type DocumentFilterParametersProps = z.infer<typeof DocumentFilterSchema.DocumentFilterZ>;

export default class DocumentFilterSchema {
  public static readonly OwnershipScopeZ = z.enum(["own", "entity", "all"]);

  public static readonly DocumentFilterJSONZ = z.object({
    name: z.string().nullish(),
    minCreatedDate: z.iso.datetime({ offset: true }).nullish(),
    maxCreatedDate: z.iso.datetime({ offset: true }).nullish(),
    query: z.string().nullish(),
    entityClass: z.string().nullish(),
    entityId: z.uuid().nullish(),
    contexts: z.array(z.string()).nullish(),
    ownershipScope: DocumentFilterSchema.OwnershipScopeZ.nullish(),
    creatorId: z.uuid().nullish(),
    creatorEntityId: z.uuid().nullish(),
    isArchived: z.boolean().nullish()
  });
  public static readonly DocumentFilterZ = z.object({
    name: z.string().optional(),
    minCreatedDate: DateSchema.type.optional(),
    maxCreatedDate: DateSchema.type.optional(),
    query: z.string().optional(),
    entityClass: z.string().optional(),
    entityId: z.instanceof(Guid).optional(),
    contexts: z.array(z.string()).optional(),
    ownershipScope: DocumentFilterSchema.OwnershipScopeZ.optional(),
    creatorId: z.instanceof(Guid).optional(),
    creatorEntityId: z.instanceof(Guid).optional(),
    isArchived: z.boolean().optional()
  });

  public static validateJSON(values: unknown): DocumentFilterParametersProps {
    return parseOrThrow(DocumentFilterSchema.DocumentFilterZ, values, {
      logError: true,
      schemaName: "DocumentFilterSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is DocumentFilterParametersProps {
    try {
      DocumentFilterSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: DocumentFilterJSON): DocumentFilterParametersProps {
    const parsed = parseOrThrow(DocumentFilterSchema.DocumentFilterJSONZ, data, {
      logError: true,
      schemaName: "DocumentFilterSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return DocumentFilterSchema.codec.decode(parsed);
  }

  public static serialize(filter: DocumentFilterParametersProps): DocumentFilterJSON {
    const parsed = DocumentFilterSchema.validateJSON(filter);
    return DocumentFilterSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    DocumentFilterSchema.DocumentFilterJSONZ,
    DocumentFilterSchema.DocumentFilterZ,
    {
      decode: (data) => ({
        name: data.name ?? undefined,
        minCreatedDate: AHDate.fromJSON(data.minCreatedDate) ?? undefined,
        maxCreatedDate: AHDate.fromJSON(data.maxCreatedDate) ?? undefined,
        query: data.query ?? undefined,
        entityClass: data.entityClass ?? undefined,
        entityId: Guid.fromJSON(data.entityId) ?? undefined,
        contexts: data.contexts ?? undefined,
        ownershipScope: data.ownershipScope ?? undefined,
        creatorId: Guid.fromJSON(data.creatorId) ?? undefined,
        creatorEntityId: Guid.fromJSON(data.creatorEntityId) ?? undefined,
        isArchived: data.isArchived ?? undefined
      }),
      encode: (filter) => ({
        name: filter.name,
        minCreatedDate: filter.minCreatedDate?.toJSON(),
        maxCreatedDate: filter.maxCreatedDate?.toJSON(),
        query: filter.query,
        entityClass: filter.entityClass,
        entityId: filter.entityId?.toJSON(),
        contexts: filter.contexts,
        ownershipScope: filter.ownershipScope,
        creatorId: filter.creatorId?.toJSON(),
        creatorEntityId: filter.creatorEntityId?.toJSON(),
        isArchived: filter.isArchived
      }),
    }
  );
}
